import type { ContactEvent } from "@/features/contact/types";
import type { Channel } from "@/types/channel";

import { v7 } from "uuid";

import { resolveWhatsappTemplatePayload } from "../generators/whatsapp-template";
import { useMockStore } from "../store";

import { getWhatsappTemplatesMock } from "./whatsapp";

import { useEventStore } from "@/stores/event";
import { makeEventFromSendMessage } from "@/utils/event";
import {
  MESSAGE_STATUS,
  MESSAGE_TYPE,
  OWNER,
  SEND_EVENT_MESSAGE_TYPE,
  type Message,
  type SendMessagePayload,
} from "@/types/message";

/**
 * Get all channels
 */
export function getChannelsMock(): Channel[] {
  const store = useMockStore.getState();

  return store.channels;
}

/**
 * Get a single channel by ID
 */
export function getChannelMock(channelId: string): Channel | undefined {
  const store = useMockStore.getState();

  return store.channels.find((c) => c.id === channelId);
}

/**
 * Update a channel
 */
export function updateChannelMock(
  channelId: string,
  updates: Partial<Channel>,
): Channel | undefined {
  const store = useMockStore.getState();
  const channel = store.channels.find((c) => c.id === channelId);

  if (!channel) return undefined;

  store.updateChannel(channelId, updates);

  return { ...channel, ...updates };
}

/**
 * Delete a channel
 */
export function deleteChannelMock(channelId: string): boolean {
  const store = useMockStore.getState();
  const exists = store.channels.some((c) => c.id === channelId);

  if (exists) {
    store.deleteChannel(channelId);

    return true;
  }

  return false;
}

/**
 * Create a channel
 */
export function createChannelMock(payload: any): Channel {
  const store = useMockStore.getState();
  const newChannel: Channel = {
    id: `channel-${Date.now()}`,
    name: payload.name || "New Channel",
    imageUrl: "",
    type: payload.type || "whatsapp",
    organizationId: "mock-org-id",
    config: payload.config || {},
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  store.addChannel(newChannel);

  return newChannel;
}

export function sendMessageMock(payload: SendMessagePayload): ContactEvent {
  if (payload.type === SEND_EVENT_MESSAGE_TYPE.TEMPLATE && payload.template) {
    return sendTemplateMessageMock(payload, payload.template);
  }

  const store = useMockStore.getState();
  const sender = store.users.find((user) => user.id === store.currentUserId);

  if (
    !sender ||
    !store.contacts.some((contact) => contact.id === payload.contactId)
  )
    throw new Error("Message sender or contact not found");
  const linked = store.messages
    .get(payload.contactId)
    ?.find((message) => message.externalId === payload.replyTo);
  const message = makeEventFromSendMessage(payload, sender, linked);

  message.id = payload.id ?? message.id;
  message.externalId = message.id;
  message.statuses = [
    {
      id: v7(),
      messageId: message.id,
      status: MESSAGE_STATUS.SENT,
      createdAt: new Date(),
      updatedAt: new Date(),
      receivedAt: null,
    },
  ];
  if (
    !store.messages
      .get(payload.contactId)
      ?.some((item) => item.id === message.id)
  )
    store.addMessage(payload.contactId, message);
  store.updateContact(payload.contactId, {
    lastMessageId: message.id,
    lastMessageProcessedAt: message.createdAt,
    lastMessageOwner: OWNER.USER,
    unreadCount: 0,
  });
  store.updateConversation(payload.contactId, { message });
  const event: ContactEvent = {
    id: message.id,
    type: "message",
    message,
    contactId: payload.contactId,
    createdAt: message.createdAt,
  };

  useEventStore.getState().addEvent(payload.contactId, event);

  return event;
}

function sendTemplateMessageMock(
  payload: SendMessagePayload,
  template: NonNullable<SendMessagePayload["template"]>,
): ContactEvent {
  const store = useMockStore.getState();
  const currentUser = store.users.find((u) => u.id === store.currentUserId);

  const templates = getWhatsappTemplatesMock(payload.channelId);
  const sentTemplate = templates.find((t) => t.id === template.id);

  const createdAt = new Date().toISOString();
  const message: Message = {
    id: v7(),
    text: null,
    owner: OWNER.USER,
    externalId: "",
    organizationId: store.organization?.id ?? "mock-org-id",
    createdAt,
    updatedAt: createdAt,
    receivedAt: null,
    statuses: [
      {
        id: v7(),
        messageId: v7(),
        status: MESSAGE_STATUS.SENT,
        createdAt: new Date(),
        updatedAt: new Date(),
        receivedAt: null,
      },
    ],
    attachments: [],
    reactions: [],
    user: currentUser ?? null,
    type: MESSAGE_TYPE.TEMPLATE,
    channel: { id: payload.channelId },
    linked: null,
    templateId: template.id,
    payload: sentTemplate
      ? resolveWhatsappTemplatePayload(sentTemplate, template.variables)
      : { templatePayload: [], deliveryPayload: [] },
  };

  store.addMessage(payload.contactId, message);
  store.updateContact(payload.contactId, {
    lastMessageProcessedAt: createdAt,
    lastMessageOwner: OWNER.USER,
  });
  const conversation = store.conversations.get(payload.contactId);

  if (conversation) {
    store.updateConversation(payload.contactId, {
      message,
      contact: {
        ...conversation.contact,
        lastMessageProcessedAt: createdAt,
        lastMessageOwner: OWNER.USER,
      },
    });
  }
  useEventStore.getState().addEvent(payload.contactId, {
    id: message.id,
    type: "message",
    message,
    contactId: payload.contactId,
    createdAt,
  });

  return {
    id: message.id,
    type: "message",
    message,
    contactId: payload.contactId,
    createdAt,
  };
}

/**
 * Upload Meta asset (mock - returns success)
 */
export function uploadMetaAssetMock(file: File): any {
  return {
    message: "OK",
    data: {
      id: `asset-${Date.now()}`,
      url: `https://mock-cdn.example.com/${file.name}`,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    },
  };
}
